import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import AnimatedBloqcubeLogo from '../../../components/Logo/AnimatedBloqcubeLogo';
import { Pie } from 'react-chartjs-2';
import { useFinanceDashboradDataQuery } from '../../../store/slices/apiSlice';

const OverallPaymentStatus = ({ clinicalStudyId, studySiteIds, ...props }) => {
  const { data: dashboardData, isLoading } = useFinanceDashboradDataQuery({
    data: { preference: 'FINANCE_TRIAL_TRANSACTION_COUNT', clinicalStudyId, studySiteIds: studySiteIds?.join(',') },
  });

  const { chartData, hasData, totalTransactions } = useMemo(() => {
    if (!dashboardData?.overall) {
      return { chartData: null, hasData: false, totalTransactions: 0 };
    }

    const { pending, succeeded, failed, total } = dashboardData.overall;
    
    // Check if there's any data to display
    const hasData = total > 0;
    
    // Filter out zero values for cleaner pie chart
    const dataPoints = [];
    const labels = [];
    const colors = [];
    const borderColors = [];

    if (succeeded > 0) {
      labels.push('Succeeded');
      dataPoints.push(succeeded);
      colors.push('#E8F5E9');
      borderColors.push('#4CAF50');
    }

    if (pending > 0) {
      labels.push('Pending');
      dataPoints.push(pending);
      colors.push('#FFF8E1');
      borderColors.push('#FFB300');
    }

    if (failed > 0) {
      labels.push('Failed');
      dataPoints.push(failed);
      colors.push('#FFEBEE');
      borderColors.push('#F44336');
    }

    const chartData = {
      labels,
      datasets: [
        {
          data: dataPoints,
          backgroundColor: colors,
          borderColor: borderColors,
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    };

    return { chartData, hasData, totalTransactions: total };
  }, [dashboardData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const percentage = ((value / totalTransactions) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    elements: {
      arc: {
        borderRadius: 4,
      },
    },
  };

  // No data state
  const NoDataDisplay = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        textAlign: 'center',
        color: 'text.secondary',
      }}
    >
      <Typography variant="" sx={{}}>
        No Transaction Data
      </Typography>
      <Typography variant="body2">
        No payment transactions found for the selected criteria.
      </Typography>
    </Box>
  );

  return (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        position: 'relative',
      }}
    >
      {isLoading && (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ height: 250, width: 250 }}>
            <AnimatedBloqcubeLogo />
          </Box>
        </Box>
      )}

      {!isLoading && dashboardData && (
        <>
          {/* Title */}
          {hasData && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              {/* <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                Overall Payment Status
              </Typography> */}
              <Typography variant="body2" color="text.secondary">
                Total Transactions: {totalTransactions}
              </Typography>
            </Box>
          )}

          {/* Chart or No Data */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {hasData ? <Pie data={chartData} options={options} /> : <NoDataDisplay />}
          </Box>
        </>
      )}
    </Box>
  );
};

export default OverallPaymentStatus;